import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { StoreDto } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { Alert } from '../Alert';

/** Search-and-pick a store (chain, name, address). Used by "New list" and "Change store". */
export function StorePicker({
  onPick,
  selectedId,
}: {
  onPick: (store: StoreDto | null) => void;
  selectedId?: string | null;
}) {
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search, 300);

  const stores = useQuery({
    queryKey: ['stores', 'search', q],
    queryFn: () => api.stores.list({ ...(q.trim() ? { q: q.trim() } : {}), limit: 25 }),
    placeholderData: (previous) => previous,
  });

  const results = stores.data?.stores ?? [];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <label htmlFor="store-picker-search" className="text-sm font-medium text-ink">
          Store
        </label>
        <input
          id="store-picker-search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search stores…"
          className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
        />
      </div>

      {stores.isError ? <Alert tone="error">{messageForError(stores.error)}</Alert> : null}

      <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
        <li>
          <button
            type="button"
            onClick={() => onPick(null)}
            aria-pressed={!selectedId}
            className={`flex min-h-11 w-full items-center rounded-lg border px-3 text-left text-sm ${
              !selectedId ? 'border-brand bg-brand-soft text-brand' : 'border-line text-ink'
            }`}
          >
            No store — I'll sort it myself
          </button>
        </li>
        {stores.isPending ? <li className="text-sm text-muted">Loading stores…</li> : null}
        {!stores.isPending && results.length === 0 ? (
          <li className="text-sm text-muted">No stores match that search.</li>
        ) : null}
        {results.map((store) => (
          <li key={store.id}>
            <button
              type="button"
              onClick={() => onPick(store)}
              aria-pressed={selectedId === store.id}
              className={`flex min-h-11 w-full flex-col items-start rounded-lg border px-3 py-1 text-left ${
                selectedId === store.id
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-line text-ink'
              }`}
            >
              <span className="text-sm font-medium">
                {store.chain} — {store.name}
              </span>
              {store.address ? <span className="text-xs text-muted">{store.address}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
